import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Pulse, Warning } from "@phosphor-icons/react";

/**
 * Compact widget for the Super-Admin Analytics page that shows AI-reply
 * health: % of AI messages that fell back to the safety-net text
 * because the LLM returned empty / raised / produced unusable chunks.
 *
 * A healthy production app should run UNDER 5% fallback rate. Per-astrologer
 * breakdown surfaces the noisiest one first — usually means their prompt
 * is too big or they're triggering a Claude refusal pattern.
 */
const RANGES = [
  { id: "24h", label: "Last 24h" },
  { id: "7d",  label: "Last 7d" },
  { id: "30d", label: "Last 30d" },
];

const rateColor = (pct) => {
  if (pct <= 2)  return { fg: "#059669", bg: "#ECFDF5", border: "#A7F3D0", label: "Healthy" };
  if (pct <= 5)  return { fg: "#CA8A04", bg: "#FEFCE8", border: "#FDE68A", label: "Watch"   };
  if (pct <= 10) return { fg: "#EA580C", bg: "#FFF7ED", border: "#FED7AA", label: "Elevated"};
  return                  { fg: "#DC2626", bg: "#FEF2F2", border: "#FCA5A5", label: "Critical"};
};

export default function AIFallbackWidget() {
  const [range, setRange] = useState("24h");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/super-admin/ai-fallback-stats", { params: { period: range } })
      .then(({ data }) => { setData(data); setErr(null); })
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [range]);

  const t = data?.totals;
  const c = rateColor(t?.rate_pct || 0);
  const top = (data?.by_astrologer || []).slice(0, 8);

  return (
    <section className="space-y-4" data-testid="ai-fallback-widget">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="label-eyebrow text-muted flex items-center gap-1.5">
          <Pulse size={13} weight="bold" /> AI reply health · fallback rate
        </div>
        <div className="flex items-center gap-1.5" data-testid="ai-fallback-range">
          {RANGES.map((r) => (
            <button key={r.id} type="button" onClick={() => setRange(r.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                range === r.id
                  ? "bg-[#3F2D1B] text-white border-[#3F2D1B]"
                  : "bg-white text-[#3F2D1B] border-soft hover:border-amber-400"
              }`}
              data-testid={`ai-fallback-range-${r.id}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="rounded-2xl p-5 bg-white border border-soft text-sm text-muted">
          Loading AI reply health…
        </div>
      )}
      {err && (
        <div className="rounded-2xl p-5 bg-white border border-rose-200 text-sm text-rose-700">
          Failed to load: {err}
        </div>
      )}

      {!loading && !err && t && (
        <div className="rounded-2xl p-5 sm:p-6 bg-white border border-soft" data-testid="ai-fallback-card">
          {/* Headline */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="label-eyebrow text-muted">Fallback rate</div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="font-serif-display text-4xl sm:text-5xl font-light" style={{ color: c.fg }}
                  data-testid="ai-fallback-rate">
                  {t.rate_pct}%
                </div>
                <div className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: c.bg, color: c.fg, border: `1px solid ${c.border}` }}>
                  {c.label}
                </div>
              </div>
              <div className="mt-1 text-xs text-muted">
                {t.fallbacks.toLocaleString("en-IN")} fallback{t.fallbacks === 1 ? "" : "s"} of {t.ai_messages.toLocaleString("en-IN")} AI messages
              </div>
            </div>
            {t.rate_pct > 5 && (
              <div className="text-xs text-amber-800 max-w-xs leading-relaxed flex items-start gap-1.5">
                <Warning size={14} weight="fill" className="text-amber-700 mt-0.5 flex-shrink-0" />
                <span>Rate is above the healthy 5% bar. Inspect the top astrologer below and trim their system prompt or check Claude refusal patterns.</span>
              </div>
            )}
          </div>

          {/* Per-astrologer breakdown */}
          {top.length > 0 ? (
            <div className="mt-5 pt-4 border-t border-soft">
              <div className="label-eyebrow text-muted mb-3">Top 8 by fallback count</div>
              <div className="space-y-2.5">
                {top.map((a) => {
                  const ac = rateColor(a.rate_pct);
                  return (
                    <div key={a.astrologer_id}
                      className="grid grid-cols-12 items-center gap-2 sm:gap-3 text-sm"
                      data-testid={`ai-fallback-astro-${a.astrologer_id}`}>
                      <div className="col-span-5 sm:col-span-4 truncate">
                        <span className="font-medium">{a.name}</span>
                        {a.technique && (
                          <span className="hidden sm:inline ml-1 text-[10px] text-muted">· {a.technique}</span>
                        )}
                      </div>
                      <div className="col-span-4 sm:col-span-5 h-2 rounded-full overflow-hidden bg-soft">
                        <div className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(100, Math.max(2, a.rate_pct))}%`,
                            background: ac.fg,
                          }} />
                      </div>
                      <div className="col-span-2 sm:col-span-2 text-right text-xs tabular-nums font-mono"
                        style={{ color: ac.fg }}>
                        {a.rate_pct}%
                      </div>
                      <div className="col-span-1 text-right text-xs tabular-nums text-muted">
                        {a.fallbacks}/{a.total}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mt-5 pt-4 border-t border-soft text-xs text-muted">
              No AI messages in this window yet — fallback rate appears as 0%.
            </div>
          )}
        </div>
      )}
    </section>
  );
}
